import { Search, SlidersHorizontal, X } from 'lucide-react';
import type { ExpenseFilters } from '../../types';
import { CATEGORIES } from '../../types';
import { getCategoryMeta } from '../../hooks/useAnalytics';
import Input from '../ui/Input';
import Select from '../ui/Select';

interface ExpenseFiltersProps {
  filters: ExpenseFilters;
  onChange: (filters: ExpenseFilters) => void;
}

const categoryOptions = [
  { value: 'all', label: 'All Categories' },
  ...CATEGORIES.map(c => ({ value: c, label: `${getCategoryMeta(c).icon} ${getCategoryMeta(c).label}` })),
];

const recurringOptions = [
  { value: 'all', label: 'All' },
  { value: 'true', label: 'Recurring' },
  { value: 'false', label: 'One-time' },
];

const hasActiveFilters = (f: ExpenseFilters) =>
  f.search || f.category !== 'all' || f.dateFrom || f.dateTo || f.recurring !== 'all';

export default function ExpenseFiltersBar({ filters, onChange }: ExpenseFiltersProps) {
  const reset = () =>
    onChange({ search: '', category: 'all', dateFrom: '', dateTo: '', recurring: 'all' });

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <SlidersHorizontal size={16} className="text-slate-400" />
        <span className="text-sm font-medium text-slate-300">Filters</span>
        {hasActiveFilters(filters) && (
          <button
            onClick={reset}
            className="ml-auto flex items-center gap-1 text-xs text-slate-400 hover:text-red-400 transition-colors"
          >
            <X size={12} />
            Clear
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Input
          placeholder="Search expenses…"
          leftIcon={<Search size={14} />}
          value={filters.search}
          onChange={e => onChange({ ...filters, search: e.target.value })}
        />

        <Select
          options={categoryOptions}
          value={filters.category}
          onChange={e => onChange({ ...filters, category: e.target.value as ExpenseFilters['category'] })}
        />

        <div className="flex gap-2">
          <Input
            type="date"
            placeholder="From"
            value={filters.dateFrom}
            onChange={e => onChange({ ...filters, dateFrom: e.target.value })}
          />
          <Input
            type="date"
            placeholder="To"
            value={filters.dateTo}
            onChange={e => onChange({ ...filters, dateTo: e.target.value })}
          />
        </div>

        <Select
          options={recurringOptions}
          value={filters.recurring}
          onChange={e => onChange({ ...filters, recurring: e.target.value as ExpenseFilters['recurring'] })}
        />
      </div>
    </div>
  );
}
